import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'

import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'

import { httpOk, GET, DELETE } from 'helpers/api'
import { formatMoney, formatDate, regexPhoneNumber } from 'helpers/helpers'

// Harakat turlari (faqat ko'rsatish)
const TX_TYPES = {
	accrued: { labelKey: 'accrued_done', sign: '+', badge: 'bg-success' },
	kpi: { labelKey: 'kpi_short', sign: '+', badge: 'bg-info' },
	bonus: { labelKey: 'bonus_word', sign: '+', badge: 'bg-success' },
	fine: { labelKey: 'fine_word', sign: '-', badge: 'bg-danger' },
	advance: { labelKey: 'advance_word', sign: '-', badge: 'bg-warning text-dark' },
	payment: { labelKey: 'payment_word', sign: '-', badge: 'bg-primary' },
}

function Account() {
	const { t } = useTranslation()

	const [data, setData] = useState([])
	const [detail, setDetail] = useState(null)
	const [selected, setSelected] = useState({})
	const [showDetail, setShowDetail] = useState(false)
	const [filterData, setFilterData] = useState({ search: '' })
	const [pagination, setPagination] = useState({ url: '/employee-account', response: null })

	async function search() {
		var p = ''
		if (filterData.search) p += (p ? '&' : '?') + 'search=' + filterData.search
		if (filterData.page) p += (p ? '&' : '?') + 'page=' + filterData.page
		if (filterData.size) p += (p ? '&' : '?') + 'size=' + filterData.size
		const res = await GET('/employee-account' + p, {}, { loader: true })
		if (httpOk(res)) {
			setData(res.data)
			setPagination({ ...pagination, response: res.data })
		}
	}

	async function openDetail(emp) {
		const res = await GET('/employee-account/detail', { employee_id: emp.id }, { loader: true })
		if (httpOk(res)) {
			setDetail(res.data)
			setSelected(emp)
			setShowDetail(true)
		}
	}

	async function removeTx(id) {
		if (!window.confirm(t('delete') + '?')) return
		const res = await DELETE('/employee-account', { id }, { loader: true })
		if (httpOk(res)) {
			openDetail(selected)
			search()
		}
	}

	function paginate(page, size) {
		setFilterData({ ...filterData, page, size })
	}

	useEffect(() => {
		search()
	}, [filterData.search, filterData.page, filterData.size]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">{t('ledger_history')}</h4>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex mb-3">
						<FilterSearch filterData={filterData} setFilterData={setFilterData} />
					</div>
					<p className="text-muted small mb-3">
						<i className="uil-info-circle me-1"></i>
						{t('ledger_info')}
					</p>

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th className="text-center" style={{ minWidth: '33px' }}>#</th>
									<th>{t('name2')}</th>
									<th>{t('phone')}</th>
									<th className="text-end">{t('accrued_word')}</th>
									<th className="text-end">{t('given_word')}</th>
									<th className="text-end">{t('fine_word')}</th>
									<th className="text-end">{t('balance_word')}</th>
								</tr>
							</thead>
							<tbody>
								{data?.data?.map((item, index) => {
									const bal = Number(item.balance)
									return (
										<tr key={index} className="tr-middle">
											<td className="text-center">{item.row_number}</td>
											<td className="text-primary cursor fw-semibold" onClick={() => openDetail(item)}>
												{item.name}
											</td>
											<td className="text-nowrap">{regexPhoneNumber(item.phone)}</td>
											<td className="text-end text-nowrap">{formatMoney(item.credit_total)}</td>
											<td className="text-end text-nowrap text-primary">{formatMoney(item.cash_given)}</td>
											<td className="text-end text-nowrap text-danger">{formatMoney(item.fine_total)}</td>
											<td className={`text-end text-nowrap fw-bold ${bal > 0 ? 'text-success' : bal < 0 ? 'text-danger' : ''}`}>
												{formatMoney(bal)}
											</td>
										</tr>
									)
								})}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination pagination={pagination} paginate={paginate} />
					}
				</div>
			</div>

			{/* DETAIL — oldi-berdi tarixi */}
			<Modal show={showDetail} animation={false} centered size="lg" backdrop="static"
				onHide={() => setShowDetail(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{detail?.employee?.name} — {t('transactions_history')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{detail &&
						<>
							<div className="d-flex justify-content-between align-items-center mb-3 p-2 rounded"
								style={{ background: 'var(--dk-surface-2, #f8f9fa)', border: '1px solid #dee2e6' }}>
								<span className="fw-semibold">{t('balance_word')}:</span>
								<span className={`fs-5 fw-bold ${detail.balance > 0 ? 'text-success' : detail.balance < 0 ? 'text-danger' : ''}`}>
									{formatMoney(detail.balance)} so'm
								</span>
							</div>

							{detail.transactions?.length > 0 ? (
								<div className="table-responsive">
									<table className="table table-bordered table-sm mb-0">
										<thead>
											<tr>
												<th>{t('date')}</th>
												<th>{t('type_word')}</th>
												<th>{t('note')}</th>
												<th className="text-end">{t('amount_word')}</th>
												<th className="text-end">{t('balance_word')}</th>
												<th></th>
											</tr>
										</thead>
										<tbody>
											{detail.transactions.map((tx, i) => {
												const cfg = TX_TYPES[tx.type] || {}
												return (
													<tr key={i}>
														<td className="text-nowrap">{formatDate(tx.trx_date, 'dd.MM.yyyy')}</td>
														<td><span className={`badge ${cfg.badge}`}>{t(cfg.labelKey)}</span></td>
														<td>
															{tx.note}
															{tx.account_name && <small className="text-muted d-block">{tx.account_name}</small>}
														</td>
														<td className={`text-end text-nowrap ${cfg.sign === '+' ? 'text-success' : 'text-danger'}`}>
															{cfg.sign} {formatMoney(tx.amount)}
														</td>
														<td className="text-end text-nowrap fw-semibold">{formatMoney(tx.running)}</td>
														<td className="text-center">
															<i className="uil-trash-alt text-danger cursor" title={t('delete')} onClick={() => removeTx(tx.id)} />
														</td>
													</tr>
												)
											})}
										</tbody>
									</table>
								</div>
							) : (
								<p className="text-muted small text-center py-3">{t('no_transactions')}</p>
							)}
						</>
					}
				</Modal.Body>
			</Modal>
		</>
	)
}

export default Account
